import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Banner,
  Button,
  Checkbox,
  Dialog,
  EmptyState,
  Field,
  Panel,
  Segmented,
  Select,
  SimpleTable,
  Spinner,
  Switch,
  Tag,
  TextArea,
  TextInput,
  useCkToast,
} from "@NP-Webber/crm-kit/dist/next/index.js";
import { salesApi, type Currency, type LeadStage, type ProductDto, type RepDto, type SalesListKey, type SalesSettings } from "../../api/salesApi";
import { ScreenHeader } from "../page/ScreenHeader";
import { useShell } from "../shell/ShellContext";
import { errorMessage, fmtMoney, fromMinor, toMinor } from "./salesUi";
import { useSalesMeta } from "./useSalesMeta";
import "./sales.css";

type Tab = "products" | "reps" | "lists" | "general";
const TABS: Array<{ value: Tab; label: string }> = [
  { value: "products", label: "קורסים ומוצרים" },
  { value: "reps", label: "נציגים ועמלות" },
  { value: "lists", label: "רשימות" },
  { value: "general", label: "כללי" },
];

const numOrUndef = (s: string) => (s.trim() === "" || !Number.isFinite(Number(s)) ? undefined : Number(s));

/* ── products ── */

function ProductDialog({ product, onClose, onSaved }: { product: ProductDto | "new" | null; onClose: () => void; onSaved: () => void }) {
  const toast = useCkToast();
  const [f, setF] = useState({ name: "", groupName: "", price: "", currency: "ILS" as Currency, active: true, sortOrder: "0", notes: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!product) return;
    if (product === "new") setF({ name: "", groupName: "", price: "", currency: "ILS", active: true, sortOrder: "0", notes: "" });
    else
      setF({
        name: product.name,
        groupName: product.groupName ?? "",
        price: fromMinor(product.priceAgorot),
        currency: product.currency,
        active: product.active,
        sortOrder: String(product.sortOrder),
        notes: product.notes ?? "",
      });
  }, [product]);
  if (!product) return null;
  const submit = async () => {
    if (!f.name.trim()) {
      toast.error("צריך שם");
      return;
    }
    setBusy(true);
    try {
      await salesApi.products.save(product === "new" ? null : product.id, {
        name: f.name.trim(),
        groupName: f.groupName.trim() || null,
        priceAgorot: toMinor(f.price) ?? 0,
        currency: f.currency,
        active: f.active,
        sortOrder: Math.round(Number(f.sortOrder) || 0),
        notes: f.notes.trim() || null,
      });
      toast.success("נשמר");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errorMessage(e, "השמירה נכשלה"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      onClose={onClose}
      size="md"
      icon="box"
      title={product === "new" ? "מוצר חדש" : "עריכת מוצר"}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            ביטול
          </Button>
          <Button variant="primary" disabled={busy} onClick={() => void submit()}>
            {busy ? "שומר…" : "שמירה"}
          </Button>
        </>
      }
    >
      <div className="sl-stack">
        <div className="sl-grid2">
          <Field label="שם הקורס / המוצר">
            <TextInput value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} autoFocus />
          </Field>
          <Field label="קבוצה" hint="למשל: קורסים, ספרים, מנויים">
            <TextInput value={f.groupName} onChange={(e) => setF({ ...f, groupName: e.target.value })} />
          </Field>
          <Field label="מטבע">
            <Segmented
              ariaLabel="מטבע"
              value={f.currency === "USD" ? "USD" : "ILS"}
              onChange={(v) => setF({ ...f, currency: v as Currency })}
              items={[
                { value: "ILS", label: "₪ שקל" },
                { value: "USD", label: "$ דולר" },
              ]}
            />
          </Field>
          <Field label={`מחיר (${f.currency === "USD" ? "$" : "₪"})`}>
            <TextInput ltr inputMode="decimal" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} />
          </Field>
          <Field label="סדר תצוגה">
            <TextInput ltr inputMode="numeric" value={f.sortOrder} onChange={(e) => setF({ ...f, sortOrder: e.target.value })} />
          </Field>
          <Field label="מצב">
            <Switch checked={f.active} onChange={(v) => setF({ ...f, active: v })} label={f.active ? "פעיל" : "לא פעיל"} />
          </Field>
        </div>
        <Field label="הערות" full>
          <TextArea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} />
        </Field>
      </div>
    </Dialog>
  );
}

function ProductsTab({ products, onSaved }: { products: ProductDto[]; onSaved: () => void }) {
  const [editing, setEditing] = useState<ProductDto | "new" | null>(null);
  const rows = [...products].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, "he"));
  return (
    <Panel title="קורסים ומוצרים" icon="box" sub="מה נמכר ובאיזה מחיר — המחיר נכנס אוטומטית לעסקה" flush actions={<Button size="sm" icon="plus" onClick={() => setEditing("new")}>מוצר חדש</Button>}>
      <SimpleTable
        ariaLabel="מוצרים"
        rows={rows}
        rowKey="id"
        onRowClick={(r: ProductDto) => setEditing(r)}
        empty="עוד אין מוצרים"
        columns={[
          { key: "name", label: "שם", render: (r: ProductDto) => <b>{r.name}</b> },
          { key: "groupName", label: "קבוצה", render: (r: ProductDto) => r.groupName || "" },
          { key: "price", label: "מחיר", align: "end", render: (r: ProductDto) => <span className="sl-num">{fmtMoney(r.priceAgorot, r.currency)}</span> },
          { key: "active", label: "מצב", render: (r: ProductDto) => (r.active ? <Tag tone="success">פעיל</Tag> : <Tag tone="neutral">לא פעיל</Tag>) },
          { key: "sortOrder", label: "סדר", align: "end" },
          { key: "notes", label: "הערות", render: (r: ProductDto) => <span className="sl-muted">{r.notes || ""}</span> },
        ]}
      />
      <ProductDialog product={editing} onClose={() => setEditing(null)} onSaved={onSaved} />
    </Panel>
  );
}

/* ── reps ── */

function RepDialog({ rep, onClose, onSaved }: { rep: RepDto | "new" | null; onClose: () => void; onSaved: () => void }) {
  const toast = useCkToast();
  const blank = { name: "", phone: "", active: true, percent: "", abroadPercent: "", partialPercent: "", netOfVat: false, hourlyRate: "", monthlyBase: "" };
  const [f, setF] = useState(blank);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!rep) return;
    if (rep === "new") setF(blank);
    else {
      const c = rep.commission ?? {};
      const s = (n: number | undefined) => (n == null ? "" : String(n));
      setF({
        name: rep.name,
        phone: rep.phone ?? "",
        active: rep.active,
        percent: s(c.percent),
        abroadPercent: s(c.abroadPercent),
        partialPercent: s(c.partialPercent),
        netOfVat: Boolean(c.netOfVat),
        hourlyRate: s(c.hourlyRate),
        monthlyBase: s(c.monthlyBase),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rep]);
  if (!rep) return null;
  const submit = async () => {
    if (!f.name.trim()) {
      toast.error("צריך שם");
      return;
    }
    setBusy(true);
    try {
      await salesApi.reps.save(rep === "new" ? null : rep.id, {
        name: f.name.trim(),
        phone: f.phone.trim() || null,
        active: f.active,
        userId: rep === "new" ? null : rep.userId,
        commission: {
          percent: numOrUndef(f.percent),
          abroadPercent: numOrUndef(f.abroadPercent),
          partialPercent: numOrUndef(f.partialPercent),
          netOfVat: f.netOfVat,
          hourlyRate: numOrUndef(f.hourlyRate),
          monthlyBase: numOrUndef(f.monthlyBase),
        },
      });
      toast.success("נשמר");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(errorMessage(e, "השמירה נכשלה"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      onClose={onClose}
      size="md"
      icon="users"
      title={rep === "new" ? "נציג חדש" : "עריכת נציג"}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            ביטול
          </Button>
          <Button variant="primary" disabled={busy} onClick={() => void submit()}>
            {busy ? "שומר…" : "שמירה"}
          </Button>
        </>
      }
    >
      <div className="sl-stack">
        <div className="sl-grid2">
          <Field label="שם">
            <TextInput value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} autoFocus />
          </Field>
          <Field label="טלפון">
            <TextInput ltr type="tel" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} />
          </Field>
          <Field label="מצב">
            <Switch checked={f.active} onChange={(v) => setF({ ...f, active: v })} label={f.active ? "פעיל" : "לא פעיל"} />
          </Field>
        </div>
        <h4 className="sl-section-title">עמלות</h4>
        <div className="sl-grid3">
          <Field label="אחוז עמלה (ישראל)">
            <TextInput ltr inputMode="decimal" value={f.percent} onChange={(e) => setF({ ...f, percent: e.target.value })} />
          </Field>
          <Field label="אחוז עמלה חו״ל" hint="ריק = כמו ישראל">
            <TextInput ltr inputMode="decimal" value={f.abroadPercent} onChange={(e) => setF({ ...f, abroadPercent: e.target.value })} />
          </Field>
          <Field label="אחוז מעסקאות חלקיות">
            <TextInput ltr inputMode="decimal" value={f.partialPercent} onChange={(e) => setF({ ...f, partialPercent: e.target.value })} />
          </Field>
          <Field label="שכר שעתי (₪)">
            <TextInput ltr inputMode="decimal" value={f.hourlyRate} onChange={(e) => setF({ ...f, hourlyRate: e.target.value })} />
          </Field>
          <Field label="בסיס חודשי (₪)">
            <TextInput ltr inputMode="decimal" value={f.monthlyBase} onChange={(e) => setF({ ...f, monthlyBase: e.target.value })} />
          </Field>
        </div>
        <Switch checked={f.netOfVat} onChange={(v) => setF({ ...f, netOfVat: v })} label="עמלה על הסכום ללא מע״מ" hint="כשמסומן — המע״מ מורד מהסכום לפני חישוב העמלה" />
      </div>
    </Dialog>
  );
}

function RepsTab({ reps, onSaved }: { reps: RepDto[]; onSaved: () => void }) {
  const [editing, setEditing] = useState<RepDto | "new" | null>(null);
  const pct = (n: number | undefined) => (n == null ? "—" : `${n}%`);
  return (
    <Panel title="נציגים ועמלות" icon="users" sub="מי מוכר, ואיך מחושבת העמלה של כל אחד" flush actions={<Button size="sm" icon="plus" onClick={() => setEditing("new")}>נציג חדש</Button>}>
      <SimpleTable
        ariaLabel="נציגים"
        rows={reps}
        rowKey="id"
        onRowClick={(r: RepDto) => setEditing(r)}
        empty="עוד אין נציגים"
        columns={[
          { key: "name", label: "שם", render: (r: RepDto) => <b>{r.name}</b> },
          { key: "phone", label: "טלפון", render: (r: RepDto) => <span className="sl-ltr">{r.phone || ""}</span> },
          { key: "active", label: "מצב", render: (r: RepDto) => (r.active ? <Tag tone="success">פעיל</Tag> : <Tag tone="neutral">לא פעיל</Tag>) },
          { key: "percent", label: "ישראל", align: "end", render: (r: RepDto) => pct(r.commission?.percent) },
          { key: "abroad", label: "חו״ל", align: "end", render: (r: RepDto) => pct(r.commission?.abroadPercent) },
          { key: "partial", label: "חלקיות", align: "end", render: (r: RepDto) => pct(r.commission?.partialPercent) },
          { key: "net", label: "ללא מע״מ", render: (r: RepDto) => (r.commission?.netOfVat ? "כן" : "") },
          { key: "base", label: "בסיס חודשי", align: "end", render: (r: RepDto) => (r.commission?.monthlyBase ? fmtMoney(r.commission.monthlyBase * 100) : "") },
        ]}
      />
      <RepDialog rep={editing} onClose={() => setEditing(null)} onSaved={onSaved} />
    </Panel>
  );
}

/* ── lists ── */

type DraftOption = { key: string | null; label: string; active: boolean; stage: LeadStage | ""; noAnswer: boolean };

const STAGE_OPTIONS = [
  { value: "", label: "ללא שינוי שלב" },
  { value: "won", label: "נסגר בהצלחה" },
  { value: "lost", label: "לא רלוונטי" },
  { value: "done", label: "טופל" },
];

function ListEditor({ listKey, title }: { listKey: SalesListKey; title: string }) {
  const toast = useCkToast();
  const { meta, reload } = useSalesMeta();
  const outcome = listKey === "sales_lead_outcome";
  const build = (): DraftOption[] =>
    [...(meta?.options[listKey] ?? [])]
      .sort((a, b) => a.order - b.order)
      .map((o) => ({
        key: o.key,
        label: o.label,
        active: o.active,
        stage: (meta?.settings.outcomeStages[o.key] as LeadStage | undefined) ?? "",
        noAnswer: Boolean(meta?.settings.noAnswerOutcomes.includes(o.key)),
      }));
  const [items, setItems] = useState<DraftOption[]>(build);
  const [busy, setBusy] = useState(false);
  const [adding, setAdding] = useState("");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => setItems(build()), [meta]);

  const patch = (i: number, p: Partial<DraftOption>) => setItems((arr) => arr.map((x, j) => (j === i ? { ...x, ...p } : x)));
  const move = (i: number, d: -1 | 1) =>
    setItems((arr) => {
      const j = i + d;
      if (j < 0 || j >= arr.length) return arr;
      const next = [...arr];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  const add = () => {
    const label = adding.trim();
    if (!label) return;
    setItems((arr) => [...arr, { key: null, label, active: true, stage: "", noAnswer: false }]);
    setAdding("");
  };

  const save = async () => {
    if (items.some((x) => !x.label.trim())) {
      toast.error("יש פריט בלי שם");
      return;
    }
    setBusy(true);
    try {
      const saved = await salesApi.saveOptions(
        listKey,
        items.map((x) => ({ key: x.key, label: x.label.trim(), active: x.active })),
      );
      if (outcome && meta) {
        // new options get their key from the server — match them back by label
        const keyOf = (x: DraftOption) => x.key ?? saved.find((s) => s.label === x.label.trim())?.key ?? null;
        const stages: Record<string, LeadStage> = {};
        const noAnswer: string[] = [];
        items.forEach((x) => {
          const k = keyOf(x);
          if (!k) return;
          if (x.stage) stages[k] = x.stage;
          if (x.noAnswer) noAnswer.push(k);
        });
        await salesApi.updateSettings({ outcomeStages: stages, noAnswerOutcomes: noAnswer });
      }
      await reload();
      toast.success("הרשימה נשמרה");
    } catch (e) {
      toast.error(errorMessage(e, "השמירה נכשלה"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel
      title={title}
      icon="list"
      sub={outcome ? "לכל תוצאה אפשר לקבוע לאיזה שלב הליד עובר, והאם היא נספרת כ«לא ענה»" : undefined}
      actions={
        <Button size="sm" variant="primary" icon="check" disabled={busy} onClick={() => void save()}>
          {busy ? "שומר…" : "שמירה"}
        </Button>
      }
    >
      <div className="sl-stack sl-stack--tight">
        <ul className="sl-optlist">
          {items.map((x, i) => (
            <li key={`${x.key ?? "new"}-${i}`} className={x.active ? undefined : "is-off"}>
              <Button size="sm" variant="ghost" icon="up" iconOnly aria-label="למעלה" disabled={i === 0} onClick={() => move(i, -1)} />
              <Button size="sm" variant="ghost" icon="down" iconOnly aria-label="למטה" disabled={i === items.length - 1} onClick={() => move(i, 1)} />
              <div className="sl-optlist__label">
                <TextInput aria-label="שם" value={x.label} onChange={(e) => patch(i, { label: e.target.value })} />
              </div>
              {outcome ? (
                <>
                  <Select className="sl-optlist__stage" aria-label="שלב הליד" options={STAGE_OPTIONS} value={x.stage} onChange={(v) => patch(i, { stage: v as LeadStage | "" })} />
                  <Checkbox checked={x.noAnswer} onChange={(v) => patch(i, { noAnswer: v })} label="נחשב לא ענה" />
                </>
              ) : null}
              <Switch checked={x.active} onChange={(v) => patch(i, { active: v })} label={x.active ? "פעיל" : "מוסתר"} />
              {x.key ? <span className="sl-optlist__key">{x.key}</span> : <Tag tone="info">חדש</Tag>}
            </li>
          ))}
        </ul>
        <div className="sl-row">
          <TextInput
            aria-label="פריט חדש"
            placeholder="פריט חדש…"
            value={adding}
            onChange={(e) => setAdding(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") add();
            }}
            style={{ maxWidth: 260 }}
          />
          <Button size="sm" icon="plus" disabled={!adding.trim()} onClick={add}>
            הוספה
          </Button>
        </div>
      </div>
    </Panel>
  );
}

/* ── general ── */

function GeneralTab({ settings }: { settings: SalesSettings }) {
  const toast = useCkToast();
  const { optionsOf, reload } = useSalesMeta();
  const [f, setF] = useState({
    vatPercent: String(settings.vatPercent),
    vatPaymentMethods: settings.vatPaymentMethods,
    vatDomesticOnly: settings.vatDomesticOnly,
    followUpDays: String(settings.followUpDays),
    defaultUsdRate: String(settings.defaultUsdRate),
  });
  const [busy, setBusy] = useState(false);
  const methods = optionsOf("sales_payment_method", { all: true });

  const save = async () => {
    const vat = Number(f.vatPercent);
    const days = Number(f.followUpDays);
    const rate = Number(f.defaultUsdRate);
    if (!Number.isFinite(vat) || vat < 0 || vat > 100) return void toast.error("אחוז מע״מ לא תקין");
    if (!Number.isFinite(days) || days < 0) return void toast.error("מספר ימים לא תקין");
    if (!(rate > 0)) return void toast.error("שער דולר לא תקין");
    setBusy(true);
    try {
      await salesApi.updateSettings({ vatPercent: vat, vatPaymentMethods: f.vatPaymentMethods, vatDomesticOnly: f.vatDomesticOnly, followUpDays: Math.round(days), defaultUsdRate: rate });
      await reload();
      toast.success("ההגדרות נשמרו");
    } catch (e) {
      toast.error(errorMessage(e, "השמירה נכשלה"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel
      title="הגדרות כלליות"
      icon="gear"
      actions={
        <Button size="sm" variant="primary" icon="check" disabled={busy} onClick={() => void save()}>
          {busy ? "שומר…" : "שמירה"}
        </Button>
      }
    >
      <div className="sl-stack">
        <div className="sl-grid3">
          <Field label="אחוז מע״מ" hint="הסכומים בעסקאות כוללים מע״מ">
            <TextInput ltr inputMode="decimal" value={f.vatPercent} onChange={(e) => setF({ ...f, vatPercent: e.target.value })} />
          </Field>
          <Field label="ימים עד שיחה אחרי הקשבה בקו" hint="מי שהקשיב בקו — מתי להתקשר אליו">
            <TextInput ltr inputMode="numeric" value={f.followUpDays} onChange={(e) => setF({ ...f, followUpDays: e.target.value })} />
          </Field>
          <Field label="שער דולר ברירת מחדל">
            <TextInput ltr inputMode="decimal" value={f.defaultUsdRate} onChange={(e) => setF({ ...f, defaultUsdRate: e.target.value })} />
          </Field>
        </div>
        <Switch checked={f.vatDomesticOnly} onChange={(v) => setF({ ...f, vatDomesticOnly: v })} label="מע״מ רק בעסקאות בישראל" hint="עסקאות חו״ל ללא מע״מ" />
        <Field label="צורות תשלום שכוללות מע״מ" hint="אם לא מסמנים אף אחת — כל צורות התשלום כוללות מע״מ">
          <div className="sl-row">
            {methods.length ? (
              methods.map((m) => (
                <Checkbox
                  key={m.value}
                  label={m.label}
                  checked={f.vatPaymentMethods.includes(m.value)}
                  onChange={(on) =>
                    setF((p) => ({ ...p, vatPaymentMethods: on ? [...p.vatPaymentMethods, m.value] : p.vatPaymentMethods.filter((x) => x !== m.value) }))
                  }
                />
              ))
            ) : (
              <span className="sl-muted">אין צורות תשלום ברשימה — הוסיפו בלשונית «רשימות»</span>
            )}
          </div>
        </Field>
      </div>
    </Panel>
  );
}

/** /sales/settings — «קורסים, נציגים והגדרות». */
export function SalesSettingsPage() {
  const [params, setParams] = useSearchParams();
  const { allowed } = useShell();
  const { meta, error, reload } = useSalesMeta();
  const tab = (TABS.some((t) => t.value === params.get("tab")) ? params.get("tab") : "products") as Tab;
  const setTab = (t: Tab) => setParams(t === "products" ? {} : { tab: t }, { replace: true });

  if (!allowed("sales.manage")) {
    return (
      <div className="sl-page">
        <ScreenHeader />
        <EmptyState icon="key" title="אין הרשאה">
          רק מנהלי מכירות יכולים לשנות את הגדרות המכירות.
        </EmptyState>
      </div>
    );
  }

  return (
    <div className="sl-page">
      <ScreenHeader sub="המוצרים שנמכרים, הנציגים והעמלות, הרשימות (סטטוסים, תוצאות שיחה, צורות תשלום, מקורות) ומע״מ" />
      <div className="sl-tabs">
        <Segmented ariaLabel="לשוניות הגדרות" value={tab} onChange={(v) => setTab(v as Tab)} items={TABS} />
      </div>
      {error ? <Banner tone="danger">{error}</Banner> : null}
      {!meta && !error ? (
        <div className="sl-busy">
          <Spinner size={26} />
        </div>
      ) : null}
      {meta && tab === "products" ? <ProductsTab products={meta.products} onSaved={() => void reload()} /> : null}
      {meta && tab === "reps" ? <RepsTab reps={meta.reps} onSaved={() => void reload()} /> : null}
      {meta && tab === "lists" ? (
        <div className="sl-stack">
          {(Object.keys(meta.listLabels) as SalesListKey[]).map((k) => (
            <ListEditor key={k} listKey={k} title={meta.listLabels[k]} />
          ))}
        </div>
      ) : null}
      {meta && tab === "general" ? <GeneralTab key={JSON.stringify(meta.settings)} settings={meta.settings} /> : null}
    </div>
  );
}

export default SalesSettingsPage;
